
// msal-custom.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, UrlTree, Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  AuthenticationResult
} from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { filter, map, take, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MsalCustomGuard implements CanActivate {
  // Runtime flag avoids multiple triggers in a single app lifetime
  private hasStartedLoginInMemory = false;

  constructor(
    private msal: MsalService,
    private broadcast: MsalBroadcastService,
    private router: Router
  ) {
    // ✅ Clear persistent flag when login completes successfully
    this.broadcast.msalSubject$
      .pipe(filter((msg: EventMessage) =>
        msg.eventType === EventType.LOGIN_SUCCESS ||
        msg.eventType === EventType.SSO_SILENT_SUCCESS
      ))
      .subscribe((msg: EventMessage) => {
        const payload = msg.payload as AuthenticationResult;
        if (payload?.account) {
          this.msal.instance.setActiveAccount(payload.account);
        }
        sessionStorage.removeItem('msal.loginInProgress');
        this.hasStartedLoginInMemory = false;
      });
  }

  canActivate(): Observable<boolean | UrlTree> {
    // ✅ Initialize MSAL FIRST to avoid 'uninitialized_public_client_application'
    return this.initializeMsal().pipe(
      // Wait until MSAL is idle (no interaction happening)
      switchMap(() =>
        this.broadcast.inProgress$.pipe(
          filter((s) => s === InteractionStatus.None),
          take(1),
          map(() => {
            const instance = this.msal.instance;

            // ✅ If we are currently processing a redirect result, DO NOT start a new login
            const hash = window.location.hash || '';
            const isProcessingRedirect =
              hash.includes('code=') ||
              hash.includes('id_token=') ||
              hash.includes('error=');

            if (isProcessingRedirect) {
              // Let MsalRedirectComponent finish processing
              return false;
            }

            // ✅ If we already have an account, ensure active account and allow
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
              if (!instance.getActiveAccount()) {
                instance.setActiveAccount(accounts[0]); // or your own selection logic
              }
              return true;
            }

            // ✅ Prevent redirect loop across reloads/HMR with a persistent flag
            const loginFlag = sessionStorage.getItem('msal.loginInProgress') === 'true';
            if (!loginFlag && !this.hasStartedLoginInMemory) {
              this.hasStartedLoginInMemory = true;
              sessionStorage.setItem('msal.loginInProgress', 'true');

              const request: RedirectRequest = {
                scopes: ['openid', 'profile', 'offline_access', 'User.Read']
                // You can also include a 'state' or 'loginHint' if needed
              };

              this.msal.loginRedirect(request);
            }

            // Block route activation while redirecting
            return false;
          })
        )
      )
    );
  }

  /** Wrap MSAL initialization in an Observable */
  private initializeMsal(): Observable<void> {
    return new Observable<void>((observer) => {
      this.msal.instance.initialize()
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((e) => {
          console.error('[MSAL] initialize error in guard:', e);
          // Complete anyway to avoid router hanging; consider navigation to error route if desired
          observer.next();
          observer.complete();
        });
    });
  }
}
